import { useCallback, useEffect, useRef, useState } from 'react';

/* ── Sharing ──────────────────────────────────────────────────────────── */

type ShareResult = 'shared' | 'copied' | 'cancelled' | 'failed';

/**
 * Share a resource: the native sheet where the browser has one, otherwise the
 * link goes to the clipboard. Cancelling the sheet is not a failure, so it gets
 * its own result and shows no confirmation.
 */
async function shareResource(title: string, url: string): Promise<ShareResult> {
    if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
        try {
            await navigator.share({ title, url });
            return 'shared';
        } catch (err) {
            if ((err as DOMException)?.name === 'AbortError') return 'cancelled';
            // Anything else (unsupported payload, permission) falls back to copying.
        }
    }
    try {
        await navigator.clipboard.writeText(url);
        return 'copied';
    } catch {
        // The async clipboard needs a secure context and a permission the page
        // may not have; the legacy path only needs the click we are already in.
        return copyByExecCommand(url) ? 'copied' : 'failed';
    }
}

function copyByExecCommand(text: string): boolean {
    try {
        const field = document.createElement('textarea');
        field.value = text;
        field.setAttribute('readonly', '');
        field.style.cssText = 'position:fixed;top:0;left:-9999px;opacity:0';
        document.body.appendChild(field);
        field.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(field);
        return ok;
    } catch {
        return false;
    }
}

/** Absolute URL for a resource, so a shared link works outside the tab. */
export function absoluteUrl(path: string): string {
    if (typeof window === 'undefined') return path;
    return new URL(path, window.location.origin).toString();
}

/* ── Action button ────────────────────────────────────────────────────── */

const ICONS = {
    view: 'M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z M12 14.75a2.75 2.75 0 1 0 0-5.5 2.75 2.75 0 0 0 0 5.5Z',
    share: 'M15 5.5a2.5 2.5 0 1 0 0 .01M6 12a2.5 2.5 0 1 0 0 .01M15 18.5a2.5 2.5 0 1 0 0 .01M8.2 10.8l4.6-2.6M8.2 13.2l4.6 2.6',
    download: 'M12 4v11m0 0l-4-4m4 4l4-4M5 19h14',
    play: 'M8 5.5v13l11-6.5-11-6.5Z',
} as const;

export type ResourceActionKind = keyof typeof ICONS;

const LABELS: Record<ResourceActionKind, string> = {
    view: 'View',
    share: 'Share',
    download: 'Download',
    play: 'Watch',
};

function ActionButton({
    kind,
    label,
    onClick,
    href,
    filled,
}: {
    kind: ResourceActionKind;
    label?: string;
    onClick?: () => void;
    href?: string;
    /** The primary action on a card is solid; the rest are outlined. */
    filled?: boolean;
}) {
    const text = label ?? LABELS[kind];
    const ring = filled
        ? 'bg-[#08080c] text-white hover:bg-[#f60]'
        : 'border border-[#d0d0d0] bg-white text-[#08080c] hover:border-[#08080c] hover:bg-[#08080c] hover:text-white';

    const inner = (
        <>
            <span
                className={`flex size-9 items-center justify-center rounded-full transition-all duration-300 group-hover:scale-[1.08] ${ring}`}
            >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
                    <path
                        d={ICONS[kind]}
                        stroke="currentColor"
                        strokeWidth="1.9"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        fill={kind === 'play' ? 'currentColor' : 'none'}
                    />
                </svg>
            </span>
            <span className="text-[11px] text-[#5e5e5e]">{text}</span>
        </>
    );

    const shell = 'group flex flex-col items-center gap-1.5';

    if (href) {
        return (
            <a href={href} download aria-label={text} className={shell}>
                {inner}
            </a>
        );
    }
    return (
        <button type="button" onClick={onClick} aria-label={text} className={shell}>
            {inner}
        </button>
    );
}

/* ── Card action row ──────────────────────────────────────────────────── */

/**
 * The View / Share / Download row under a resource card. Share confirms inline
 * rather than through an alert, because the card is often one of twelve.
 */
export function CardActions({
    title,
    shareUrl,
    onView,
    downloadHref,
    viewLabel,
}: {
    title: string;
    shareUrl: string;
    onView: () => void;
    /** Omitted for gallery items, which are viewed and shared but not downloaded. */
    downloadHref?: string;
    viewLabel?: string;
}) {
    const [note, setNote] = useState<string | null>(null);
    const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => () => void (timer.current && clearTimeout(timer.current)), []);

    const share = useCallback(async () => {
        const result = await shareResource(title, shareUrl);
        if (result === 'cancelled') return;
        setNote(result === 'copied' ? 'Link copied' : result === 'failed' ? 'Could not share' : 'Shared');
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(() => setNote(null), 2200);
    }, [title, shareUrl]);

    return (
        <div className="mt-4">
            <div className="flex items-start justify-center gap-6">
                <ActionButton kind="view" label={viewLabel} onClick={onView} filled={!downloadHref} />
                <ActionButton kind="share" onClick={share} />
                {downloadHref && <ActionButton kind="download" href={downloadHref} filled />}
            </div>
            {/* Reserved line, so confirming a share never shifts the grid. */}
            <p aria-live="polite" className="mt-1.5 h-4 text-center text-[11px] font-semibold text-[#f60]">
                {note}
            </p>
        </div>
    );
}

/* ── Lightbox ─────────────────────────────────────────────────────────── */

export interface LightboxContent {
    title: string;
    image: string;
    /** Present for gallery videos — embedded in place of the still. */
    videoUrl?: string;
    /**
     * The document itself — a PDF, or the full-size image the cover was made
     * from. Without it the lightbox can only show the cover thumbnail, which is
     * a picture of the first page rather than the thing someone asked to read.
     */
    file?: string;
    caption?: string;
}

/**
 * Whether a PDF will actually render inside a frame.
 *
 * Cloudinary serves `raw` assets as `application/octet-stream` with `nosniff`,
 * so a browser downloads them rather than displaying them — framing one leaves
 * a blank rectangle. The legacy library on `files.gavel.ng` sends a real
 * `application/pdf` and frames fine.
 */
export function canEmbedPdf(url: string | undefined): boolean {
    return !!url && !/\/raw\/upload\//.test(url);
}

/** What a file is, from its extension. The path may carry a query, so stop at one. */
export function fileKind(url: string | undefined): 'pdf' | 'image' | 'other' {
    const ext = /\.([a-z0-9]{2,5})(?:[?#]|$)/i.exec(url ?? '')?.[1].toLowerCase();
    if (ext === 'pdf') return 'pdf';
    if (ext && ['jpg', 'jpeg', 'png', 'webp', 'gif', 'avif', 'svg'].includes(ext)) return 'image';
    return 'other';
}

/** Full-screen preview for a cover or a gallery item. Closes on Esc or backdrop. */
export function Lightbox({ item, onClose }: { item: LightboxContent | null; onClose: () => void }) {
    useEffect(() => {
        if (!item) return;
        const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
        document.addEventListener('keydown', onKey);
        // The page scrolls inside .page-scroll-root, not the body, so that is
        // what has to be frozen behind the overlay.
        const scroller = document.querySelector<HTMLElement>('.page-scroll-root');
        const prev = scroller?.style.overflowY ?? '';
        if (scroller) scroller.style.overflowY = 'hidden';
        return () => {
            document.removeEventListener('keydown', onKey);
            if (scroller) scroller.style.overflowY = prev;
        };
    }, [item, onClose]);

    if (!item) return null;

    const kind = fileKind(item.file);
    // A PDF the browser would only download is shown as its cover plus the link below.
    const framePdf = kind === 'pdf' && canEmbedPdf(item.file);

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-label={item.title}
            onClick={onClose}
            className="fixed inset-0 z-[3000] flex items-center justify-center bg-black/85 px-5 py-10 backdrop-blur-sm"
        >
            <button
                type="button"
                aria-label="Close preview"
                onClick={onClose}
                className="absolute right-5 top-5 flex size-11 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 lg:right-10 lg:top-10"
            >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
                    <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
                </svg>
            </button>

            <figure
                onClick={(e) => e.stopPropagation()}
                className={`flex max-h-full w-full flex-col items-center gap-5 ${
                    framePdf ? 'max-w-[1180px]' : 'max-w-[1000px]'
                }`}
            >
                {item.videoUrl ? (
                    <div className="aspect-video w-full overflow-hidden rounded-[10px] bg-black">
                        <iframe
                            src={item.videoUrl}
                            title={item.title}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; picture-in-picture"
                            allowFullScreen
                            className="size-full border-0"
                        />
                    </div>
                ) : framePdf ? (
                    // The browser's own PDF viewer. Some mobile browsers refuse to
                    // render one in a frame and leave it blank, so the link below
                    // is always offered rather than kept for when something fails.
                    <div className="h-[78vh] w-full overflow-hidden rounded-[10px] bg-[#1c1c1c]">
                        <iframe src={item.file} title={item.title} className="size-full border-0" />
                    </div>
                ) : (
                    <img
                        src={kind === 'image' ? item.file : item.image}
                        alt={item.title}
                        onError={(e) => {
                            // Fall back to the cover if the full-size file will not load.
                            const img = e.currentTarget;
                            if (img.src !== item.image) img.src = item.image;
                        }}
                        className="max-h-[70vh] w-auto max-w-full rounded-[10px] object-contain"
                    />
                )}
                <figcaption className="max-w-[720px] text-center">
                    <p className="text-[15px] font-bold leading-[1.5] text-white lg:text-[17px]">{item.title}</p>
                    {item.caption && <p className="mt-2 text-[13px] leading-[1.6] text-white/70">{item.caption}</p>}
                    {item.file && (
                        <a
                            href={item.file}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-3 inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#f60] hover:underline"
                        >
                            Open in a new tab
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
                                <path
                                    d="M14 4h6v6M20 4l-9 9M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </a>
                    )}
                </figcaption>
            </figure>
        </div>
    );
}
